import { useCallback, useState, useEffect } from "react";
import {
  Badge,
  Button,
  Flex,
  IconButton,
  Spinner,
  Text,
} from "@radix-ui/themes";
import {
  Cross1Icon,
  ImageIcon,
  PlusIcon,
  UploadIcon,
} from "@radix-ui/react-icons";
import { Fallback, Image, Root } from "@radix-ui/react-avatar";

import styles from "./styles.module.scss";
import { validateFile } from "@/utils/fileValidation";

export type uploadFileVariant = "basic" | "preview" | "options";

export type UploadQuackchatAssestResponse = {
  path: string;
  name: string;
};

export function KnowledgeFileUploadComponent({
  defaultFileName,
  onFileUpload,
  onSuccess,
  variant = "basic",
  fileSrc,
  choices,
  accept,
  maxSizeInBytes,
}: {
  defaultFileName?: string;
  onFileUpload: (file: File) => Promise<UploadQuackchatAssestResponse>;
  onSuccess: (filePath: string, option?: boolean) => void;
  variant?: uploadFileVariant;
  fileSrc?: string;
  choices?: { label: string; value: string }[];
  accept?: string[];
  maxSizeInBytes?: number;
}) {
  const [fileName, setFileName] = useState<string | undefined>(defaultFileName);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedCover, setSelectedCover] = useState<string | undefined>(
    choices?.some((c) => c.value === fileSrc) ? fileSrc : undefined
  );

  useEffect(() => {
    if (!fileSrc) {
      setSelectedCover(undefined);
      !defaultFileName && setFileName(undefined);
      return;
    }
    if (choices?.some((c) => c.value === fileSrc)) {
      setSelectedCover(fileSrc);
      setFileName(undefined);
    } else {
      setSelectedCover(undefined);
      if (fileSrc !== fileName) {
        setFileName(fileSrc.split("/").pop());
      }
    }
    setError(undefined);
  }, [fileSrc, choices, fileName]);

  const handleFileUpload = useCallback(
    async (file: File) => {
      try {
        validateFile(file, {
          accept,
          maxSizeInBytes,
        });

        setError(undefined);
        setLoading(true);

        const res = await onFileUpload(file);

        setSelectedCover(undefined);
        setFileName(res.name);

        onSuccess(res.path);
      } catch (e) {
        console.error(e);
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError("Failed to upload file, try again");
        }
      } finally {
        setLoading(false);
      }
    },
    [onFileUpload, onSuccess]
  );

  const handleOnClicked = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept?.join(",") ?? "";
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        void handleFileUpload(file);
      }
    };
    input.click();
  }, [handleFileUpload, accept]);

  const handleClear = useCallback(() => {
    setSelectedCover(undefined);
    setFileName(undefined);
    onSuccess("");
  }, [onSuccess]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files[0]) {
        void handleFileUpload(files[0]);
      }
    },
    [handleFileUpload]
  );

  if (variant === "options") {
    return (
      <>
        <Flex direction="row" gap="2" wrap="wrap">
          {choices?.map((choice) => {
            const isSelected = selectedCover === choice.value && !fileName;
            return (
              <Flex
                key={choice.value}
                direction="column"
                align="center"
                justify="center"
                style={{
                  width: "58px",
                  height: "58px",
                  borderRadius: "100%",
                  border: isSelected ? "1px solid var(--gray-12)" : "none",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setSelectedCover(choice.value);
                  setFileName(undefined);
                  onSuccess(choice.value, true);
                }}
              >
                <img
                  src={choice.value}
                  alt={choice.label}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transform: "scale(0.75)",
                    border:
                      "2px solid color-mix(in srgb, var(--gray-10), transparent 70%)",
                    borderRadius: "100%",
                  }}
                />
              </Flex>
            );
          })}

          <Flex
            direction="column"
            align="center"
            justify="center"
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "100%",
              border: fileName
                ? "2.5px solid var(--gray-12)"
                : isDragging
                ? "2.5px dashed var(--accent-2)"
                : "none",
              cursor: "pointer",
              backgroundColor: isDragging ? "var(--accent-2)" : undefined,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleOnClicked();
            }}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {!fileName ? (
              loading ? (
                <Spinner loading={true} size={"1"}>
                  <PlusIcon />
                </Spinner>
              ) : (
                <PlusIcon />
              )
            ) : (
              <img
                src={fileSrc}
                alt="uploaded cover"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "100%",
                  transform: "scale(0.75)",
                  border: "2px solid var(--gray-a5)",
                }}
              />
            )}
          </Flex>
        </Flex>

        {error && (
          <Text size={"1"} color={"red"} className={styles.errorText}>
            {error}
          </Text>
        )}
      </>
    );
  }

  if (variant === "preview") {
    return (
      <>
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap="2"
          className={styles.preview}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: isDragging ? "2px dashed blue" : undefined,
            backgroundColor: isDragging ? "var(--accent-2)" : undefined,
          }}
        >
          <Flex gap="2" position="relative">
            <Root>
              <Image
                src={fileSrc}
                alt="preview"
                width={60}
                height={60}
                onClick={handleOnClicked}
              />
              <Fallback
                className={styles.avatarFallback}
                delayMs={600}
                onClick={handleOnClicked}
              >
                <ImageIcon />
              </Fallback>
            </Root>
            <IconButton
              size="1"
              color="gray"
              variant="ghost"
              onClick={handleClear}
              className={styles.clearIcon}
            >
              <Cross1Icon width="10" height="10" />
            </IconButton>
          </Flex>
        </Flex>
        {!loading ? (
          <Button
            size={"1"}
            color={"gray"}
            variant={"ghost"}
            onClick={handleOnClicked}
          >
            <UploadIcon />
            Upload
          </Button>
        ) : (
          <Spinner loading={loading} size={"1"}>
            <PlusIcon />
          </Spinner>
        )}

        {error && (
          <Text size={"1"} color={"red"}>
            {error}
          </Text>
        )}
      </>
    );
  }

  return (
    <>
      {fileName != null ? (
        <Badge size="2" color="gray" style={{ maxWidth: "240px", gap: "8px" }}>
          <Text truncate>{fileName}</Text>

          <IconButton
            size="1"
            color="gray"
            variant="ghost"
            onClick={handleClear}
          >
            <Cross1Icon width="10" height="10" />
          </IconButton>
        </Badge>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={" flex"}
        >
          <Button
            size={"1"}
            color={isDragging ? "blue" : "gray"}
            variant={isDragging ? "soft" : "ghost"}
            className={
              " w-auto h-52 border border-dashed border-gray-200 flex-grow"
            }
            highContrast
            onClick={handleOnClicked}
            style={{
              borderColor: isDragging ? "var(--blue-9)" : undefined,
              borderWidth: isDragging ? "2px" : undefined,
            }}
          >
            <Spinner loading={loading} size={"1"}>
              <PlusIcon />
            </Spinner>
            {loading
              ? "Uploading ..."
              : isDragging
              ? "Drop file here"
              : "Upload file"}
          </Button>
        </div>
      )}

      {error && (
        <Text size={"1"} color={"red"}>
          {error}
        </Text>
      )}
    </>
  );
}
